/**
 * NEPTERRA EDITORIAL SECTION
 * Heavy editorial scroll motion with vertical line drift
 */

class NepterraEditorial {
  constructor(section) {
    this.section = section;
    this.logo = section.querySelector('.nepterra-logo');
    this.verticalLine = section.querySelector('.nepterra-vertical-line');
    this.cards = [];
    this.mobileCards = [];
    this.hasRevealed = false;
    this.maxDrift = parseFloat(section.dataset.maxDrift || 90);
    this.lineDrift = parseFloat(section.dataset.lineDrift || 20);
    this.isMobile = window.innerWidth < 1024;
    this.currentScroll = 0;
    this.targetScroll = 0;
    this.rafId = null;
    
    this.init();
  }

  init() {
    this.setupCards();
    this.setupRevealObserver();
    this.setupScrollListener();
    this.handleResize();
    this.startAnimationLoop();
  }

  setupCards() {
    const desktopCards = this.section.querySelectorAll('.nepterra-card');
    const cardCount = desktopCards.length;
    
    desktopCards.forEach((card, index) => {
      let driftMultiplier = 1.0;
      let isCenterCard = false;
      
      if (cardCount === 3) {
        driftMultiplier = index === 1 ? 0.5 : 1.0;
        isCenterCard = index === 1;
      } else if (cardCount === 4) {
        driftMultiplier = index < 2 ? 0.75 : 1.0;
      } else if (cardCount === 5) {
        const multipliers = [1.0, 0.8, 0.4, 0.8, 1.0];
        driftMultiplier = multipliers[index] || 1.0;
        isCenterCard = index === 2;
      }
      
      this.cards.push({
        element: card,
        driftMultiplier: driftMultiplier,
        isCenterCard: isCenterCard,
        hasTransformX: this.hasTranslateX(card),
        currentDrift: 0,
        targetDrift: 0
      });
    });

    const mobileCards = this.section.querySelectorAll('.nepterra-mobile-card');
    mobileCards.forEach(card => {
      this.mobileCards.push({
        element: card,
        driftMultiplier: 0.6,
        currentDrift: 0,
        targetDrift: 0
      });
    });
  }

  hasTranslateX(card) {
    const style = window.getComputedStyle(card);
    const transform = style.transform;
    
    if (transform && transform !== 'none') {
      const matrix = new DOMMatrix(transform);
      return matrix.m41 !== 0;
    }
    return false;
  }

  setupRevealObserver() {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasRevealed) {
          this.reveal();
          this.hasRevealed = true;
        }
      });
    }, observerOptions);

    observer.observe(this.section);
  }

  reveal() {
    const headline = this.section.querySelector('.nepterra-headline');
    const tagline = this.section.querySelector('.nepterra-tagline');

    if (this.logo) this.logo.classList.add('is-revealed');
    if (this.verticalLine) {
      setTimeout(() => this.verticalLine.classList.add('is-revealed'), 200);
    }
    if (headline) headline.classList.add('is-revealed');
    if (tagline) tagline.classList.add('is-revealed');

    // Cards reveal with stagger
    const allCards = this.isMobile ? this.mobileCards : this.cards;
    allCards.forEach((card, index) => {
      setTimeout(() => {
        card.element.classList.add('is-revealed');
      }, 350 + (index * 80));
    });
  }

  getTransformWithOffset(card, offset) {
    if (this.isMobile) {
      return `translateY(${offset}px)`;
    }
    
    if (card.hasTransformX) {
      return `translate(-50%, ${offset}px)`;
    }
    return `translateY(${offset}px)`;
  }

  setupScrollListener() {
    window.addEventListener('scroll', () => {
      this.targetScroll = this.calculateScrollProgress();
    }, { passive: true });
  }

  calculateScrollProgress() {
    const rect = this.section.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    if (rect.bottom < 0 || rect.top > viewportHeight) {
      return this.currentScroll;
    }

    const sectionHeight = rect.height;
    const scrollRange = viewportHeight + sectionHeight;
    const scrolled = viewportHeight - rect.top;
    const progress = Math.max(0, Math.min(1, scrolled / scrollRange));
    
    return (0.5 - progress) * 2;
  }

  startAnimationLoop() {
    const lerp = (start, end, factor) => start + (end - start) * factor;
    
    const animate = () => {
      // Heavy editorial interpolation
      this.currentScroll = lerp(this.currentScroll, this.targetScroll, 0.06);
      
      const baseDrift = this.currentScroll * this.maxDrift;
      const lineDriftAmount = this.currentScroll * this.lineDrift;
      
      // Update vertical line with subtle drift
      if (this.verticalLine && this.verticalLine.classList.contains('is-revealed')) {
        this.verticalLine.style.transform = `translateY(${lineDriftAmount}px)`;
      }
      
      if (this.isMobile) {
        this.updateMobileCards(baseDrift);
      } else {
        this.updateDesktopCards(baseDrift);
      }
      
      this.rafId = requestAnimationFrame(animate);
    };
    
    animate();
  }

  updateDesktopCards(baseDrift) {
    this.cards.forEach(card => {
      if (!card.element.classList.contains('is-revealed')) return;
      
      const targetDrift = baseDrift * card.driftMultiplier;
      
      // Center card has safety clamp to prevent tagline overlap
      if (card.isCenterCard) {
        // Allow downward drift fully, limit upward drift
        card.targetDrift = targetDrift > 0 ? targetDrift : Math.max(targetDrift, -60);
      } else {
        // All other cards drift freely
        card.targetDrift = targetDrift;
      }
      
      // Heavy smooth interpolation
      card.currentDrift += (card.targetDrift - card.currentDrift) * 0.08;
      
      if (card.hasTransformX) {
        card.element.style.transform = `translate(-50%, ${card.currentDrift}px)`;
      } else {
        card.element.style.transform = `translateY(${card.currentDrift}px)`;
      }
    });
  }

  updateMobileCards(baseDrift) {
    this.mobileCards.forEach(card => {
      if (!card.element.classList.contains('is-revealed')) return;
      
      const targetDrift = baseDrift * card.driftMultiplier;
      // Mobile cards drift freely
      card.targetDrift = targetDrift;
      
      card.currentDrift += (card.targetDrift - card.currentDrift) * 0.08;
      
      card.element.style.transform = `translateY(${card.currentDrift}px)`;
    });
  }

  handleResize() {
    let resizeTimeout;
    
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 1024;
        
        if (wasMobile !== this.isMobile) {
          this.cards.forEach(card => {
            card.currentDrift = 0;
            card.targetDrift = 0;
            card.element.style.transform = '';
          });
          this.mobileCards.forEach(card => {
            card.currentDrift = 0;
            card.targetDrift = 0;
            card.element.style.transform = '';
          });
          
          if (this.verticalLine) {
            this.verticalLine.style.transform = 'translateY(0)';
          }
          
          this.currentScroll = 0;
          this.targetScroll = this.calculateScrollProgress();
        }
      }, 150);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.nepterra-how-she-lives');
  sections.forEach(section => {
    new NepterraEditorial(section);
  });
});

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.nepterra-how-she-lives');
    if (section) {
      new NepterraEditorial(section);
    }
  });
}